<template>
  <FriedMessage
    v-if="showError"
    id="error"
    data-external="eid-error-message"
    :title="$t('something-went-wrong')"
    :type="MessageType.Error"
  >
    {{ $t('error-generating-qr-code') }}
  </FriedMessage>
  <div v-else-if="isSigning" class="signing-loader">
    <FriedParagraph>{{ $t('waiting-for-signature') }}</FriedParagraph>
    <FriedSpinner />
  </div>
  <div v-else class="bankid-qr-code-container" data-external="eid-modal-bankid-qr-code-container">
    <div class="instructions-container">
      <ol data-external="eid-modal-bankid-instructions-container">
        <li>{{ $t('open-bankid-app-in-phone') }}</li>
        <li>{{ $t('click-the-qr-scanner') }}</li>
        <li>{{ $t('use-the-camera') }}</li>
        <li>{{ $t('verify-yourself-with-bankid') }}</li>

        <FriedLink
          v-if="!bankId.isV6"
          id="toggle-qr-code-button"
          data-external="toggle-qr-code-button-small-screen"
          class="ssn-link"
          @click="$emit('setSSN')"
        >
          {{ $t('or-enter-personal-number') }}
        </FriedLink>
      </ol>
    </div>
    <div class="qr-code-container">
      <img
        v-if="qrCodeUrl"
        alt="qr-code"
        :src="qrCodeUrl"
        class="bankid-qr-code"
        data-external="eid-modal-bankid-qr-code-image"
      />
      <FriedSkeleton v-else-if="showQRCodeLoader" class="bankid-qr-code" />
    </div>
  </div>
</template>

<script lang="ts">
import QRCode from 'qrcode';
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { computedAsync } from '@vueuse/core';
import { MessageType } from '@getaccept/fried-tofu';
import type { BankId } from '../../../types/bankid';
import { HintCodePending } from '../../../types/enums/bankid-se';

export default defineComponent({
  props: {
    bankId: {
      type: Object as PropType<BankId>,
      required: true,
    },
  },
  emits: ['setSSN'],
  setup(props) {
    const isSigning = computed(() => props.bankId.hintCode === HintCodePending.userSign);
    const showError = computed(() => qrCodeUrl.value === null);
    const showQRCodeLoader = computed(() => props.bankId.isLoading && !qrCodeUrl.value);

    const qrCodeUrl = computedAsync<string>(async () => {
      try {
        return await QRCode.toDataURL(props.bankId.qrCode, { margin: 0 });
      } catch (error) {
        return '';
      }
    }, null);

    return {
      isSigning,
      showError,
      showQRCodeLoader,
      qrCodeUrl,
      MessageType,
    };
  },
});
</script>

<style lang="scss" scoped>
$qr-size: 145px;

.signing-loader {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-height: fit-content;
  gap: var(--spacing-50);
  margin-top: var(--spacing-100);
}

.bankid-qr-code-container {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-100);
  padding: 0 var(--spacing-100);
  height: 100%;

  @media screen and (max-width: $xs) {
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-200);
  }

  .instructions-container {
    align-self: flex-start;
    width: 100%;

    ol {
      margin-block-end: 0;
      margin-block-start: 0;
      padding-inline-start: 0;
      list-style-position: inside;

      li {
        padding-bottom: var(--spacing-25);
      }
    }
  }

  .qr-code-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    height: 100%;

    .bankid-qr-code {
      width: $qr-size;
      height: $qr-size;
    }
  }

  .ssn-link {
    margin-top: var(--spacing-50);
  }
}
</style>
